
import { Component, Mixins, Prop, Watch, Vue } from 'vue-property-decorator';
import LoadableMixin from '@/mixins/LoadableMixin';
import DeviceHistoryGrid from './DeviceHistoryGrid.vue';
import DeviceHistoryMap from './DeviceHistoryMap.vue';
import api from '@/modules/api';
import { DeviceInfo, DeviceType, DeviceMapData } from '@/types/device-info';
import { HistoryData } from '@/types/history';
import { getErrorMessage } from '@/utils/common';
import _get from 'lodash.get';
import * as dlg from '@/modules/dlg';
import { mutations } from '@/store/types';
import { LoadDeviceActionType, UpdateDeviceActionType } from '@/types/store/actions';
import { actions } from '../../store/types';
import { Action, Mutation } from 'vuex-class';
import { validatePrivilege } from '@/utils/devices';
import { DevicePrivileges } from '@/constants/device_privileges';
import DeviceMapDrawer from '../geo-fence/DeviceMapDrawer.vue';
import { SetDeviceMutationType } from '@/types/store/mutations';
// import { LMap } from 'vue2-leaflet';

@Component({
    mixins: [LoadableMixin],

    components: {
        DeviceHistoryGrid,
        DeviceHistoryMap,
        DeviceMapDrawer,
    },
})
export default class DeviceHistory extends Mixins<LoadableMixin>(LoadableMixin) {
    locations: HistoryData[] = [];
    historyViewType = 'map'; // map | grid
    drawMapArea = true;
    page = 1;
    perPage = 10;
    total = 0;
    isMapModal = false;
    isTrigger = false;
    protected validatePrivilege = validatePrivilege;
    protected DevicePrivileges = DevicePrivileges;

    deviceMapData: DeviceMapData = {};

    @Prop({ required: true })
    device!: DeviceInfo;

    @Action(actions.LOAD_DEVICE)
    loadDevice!: LoadDeviceActionType;

    @Action(actions.UPDATE_DEVICE)
    updateDevice!: UpdateDeviceActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    DeviceTypeenum = DeviceType;

    showMapModal() {
        this.isMapModal = true;
    }

    async updateGeoFence() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.updateGeoFence(
                this.device.deviceUUID,
                this?.device?.geoFence?.enable === true ? 'off' : 'on',
            );
            this.setCurrentDevice({
                device: {
                    ...this.device,
                    geoFence: {
                        ...this.device.geoFence,
                        enable: this.device?.geoFence?.enable !== undefined ? !this.device.geoFence.enable : false,
                        liveTrackingUnable: this.device?.geoFence?.liveTrackingUnable || false,
                        trackingTime: this.device?.geoFence?.trackingTime || 0,
                    },
                },
            });
            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async deleteGeoFence() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.deleteGeoFence(this.device.deviceUUID);
            this.setCurrentDevice({
                device: {
                    ...this.device,
                    geoFence: { enable: false, liveTrackingUnable: false, trackingTime: 0 },
                    mapBoundaryPoints: [],
                },
            });
            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    validateRestricted() {
        return this.validatePrivilege(this.device.privilege, [DevicePrivileges.SuperOwner, DevicePrivileges.Owner]);
    }

    async setValueOfPolyline(childData: []) {
        this.device.mapBoundaryPoints = childData;
        this.drawMapArea = false;
        if (childData.length) {
            await this.updateDevice({
                device: {
                    _id: this.device._id,
                    mapBoundaryPoints: childData,
                },
            });
        }
        // try {
        //     const response = await api.createGeoFence(
        //         this.device.deviceUUID,
        //         false,
        //         childData,
        //     );
        //     Vue.toasted.success(`Saved successfully`);
        // } catch (error) {
        //     Vue.toasted.error(getErrorMessage(error));
        // } finally {
        //     this.$store.commit(mutations.LOADING, { loading: false });
        // }
    }

    @Watch('device', { immediate: true, deep: true })
    async onDeviceChange(device: DeviceInfo, prevDevice: DeviceInfo) {
        if (_get(device, '_id') !== _get(prevDevice, '_id')) {
            this.page = 1;
            this.perPage = 10;
            this.total = 0;
            if (this.historyViewType === 'grid') {
                this.fetchLocation(device);
            }
        }
    }

    async fetchLocation(device?: DeviceInfo) {
        device = device || this.device;
        if (!device) return;

        this.setLoading(true);
        try {
            const response = await api.getDeviceHistory(device.deviceUUID, { page: this.page, perPage: this.perPage });

            this.locations = response.data;

            // const maxTotal = this.perPage * 5;
            // this.total = response.pagination.total > maxTotal ? maxTotal : response.pagination.total;

            this.total = response.pagination.total;
        } catch (error) {
            this.$toasted.error(`Unable to load history for device ${device.name}`);
        } finally {
            this.setLoading(false);
        }
    }

    async exportHistoryCsv() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const res = await api.exportHistoryCsv(this.device.deviceUUID);
            const blob = new Blob([res.data], { type: _get(res, 'headers.content-type') || 'text/csv' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `device-history-${this.device.deviceUUID}.csv`;
            link.click();
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async clearHistory() {
        const confirmed = await dlg.confirm(
            'Are you sure you want to clear the history, this action cannot be undone.',
            'Clear history',
        );
        if (!confirmed) return;

        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            let deviceUUID = this.device.deviceUUID;
            await api.clearDeviceHistory(deviceUUID as string);
            this.page = 1;
            this.perPage = 10;
            this.total = 0;
            const response = await api.getDeviceHistory(deviceUUID, { page: this.page, perPage: this.perPage });
            this.locations = response.data;
            Vue.toasted.success(`Cleared successfully`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    onTableChange(page: number, itemsPerPage: number) {
        this.page = page;
        this.perPage = itemsPerPage;
        this.fetchLocation(this.device);
    }

    async handleSubmit(liveTrackingUnable: boolean, trackingTime: string) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.createGeoFenceRoles(this.device.deviceUUID, trackingTime, liveTrackingUnable);
            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
}
